import React, { FC, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import Spinner from '../../../components/bootstrap/Spinner';
import { getLangWithKey, getOtherLanguage, ILang } from '../../../lang';
import showNotification from '../../../components/extras/showNotification';
import Icon from '../../../components/icon/Icon';

interface ILoginHeaderProps {
	t: TFunction;
}
const LoginHeader: FC<ILoginHeaderProps> = ({ t }) => {
	return (
		<>
			<div className='text-center h1 fw-bold mt-5'>{t('LoginHeader')},</div>
			<div className='text-center h6 text-muted mb-5'>{t('LoginSubText')}</div>
		</>
	);
};

const ForgetPassword = () => {
	const { darkModeStatus } = useDarkMode();

	const navigate = useNavigate();

	const auth = getAuth();
	const { t, i18n } = useTranslation('translation');

	const styledBtn: IButtonProps = {
		color: 'light',
		hoverShadow: 'default',
		isLight: true,
		size: 'lg',
	};

	const changeLanguage = (lng: ILang['key']['lng']) => {
		i18n.changeLanguage(lng).then();
		showNotification(
			<span className='d-flex align-items-center'>
				<Icon icon={getLangWithKey(lng)?.icon} size='lg' className='me-1' />
				<span>{getLangWithKey(lng)?.message}</span>
			</span>,
			'',
		);
	};

	const otherLanguage = useMemo(() => {
		return getOtherLanguage(i18n.language as ILang['key']['lng']);
	}, [i18n.language]);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginEmail: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: { loginEmail?: string; loginPassword?: string } = {};

			if (!values.loginEmail) {
				errors.loginEmail = 'Required';
			}

			if (!values.loginPassword) {
				errors.loginPassword = 'Required';
			}

			return errors;
		},
		validateOnChange: false,
		onSubmit: () => {},
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const handleContinue = () => {
		setIsLoading(true);
		const email = formik.values.loginEmail;
		const functions = getFunctions();
		const checkBusinessByEmail = httpsCallable(functions, 'checkBusinessByEmail');
		checkBusinessByEmail({ email, language: i18n.language })
			.then(() => {
				sendPasswordResetEmail(auth, email)
					.then(async () => {
						showNotification(t('PasswordResetEmailSent'), '', 'success');
						navigate('/login');
					})
					.catch((error) => {
						console.error('Error during signIn', error);
						showNotification(t('ErrorSendingPasswordResetEmail'), '', 'danger');
						setIsLoading(false);
					});
			})
			.catch(({ message }) => {
				formik.setFieldError('loginEmail', message);
				setIsLoading(false);
			});
	};

	return (
		<PageWrapper isProtected={false} title='Login' className='bg-dark'>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card className='shadow-3d-dark' data-tour='login-page'>
							<CardBody>
								<div className='text-end'>
									<Button
										{...styledBtn}
										icon={otherLanguage.icon}
										onClick={() => changeLanguage(otherLanguage.lng)}
									/>
								</div>
								<div className='text-center my-5'>
									<Link
										to='/'
										className={classNames(
											'text-decoration-none  fw-bold display-2',
											{
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											},
										)}
										aria-label='Facit'>
										<Logo isLogin />
									</Link>
								</div>

								<LoginHeader t={t} />

								<form className='row g-4'>
									<div className='col-12'>
										<FormGroup id='loginEmail' isFloating label={t('Email')}>
											<Input
												type='email'
												value={formik.values.loginEmail}
												isTouched={formik.touched.loginEmail}
												invalidFeedback={formik.errors.loginEmail}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
												}}
											/>
										</FormGroup>
									</div>
									<div className='col-12'>
										<Button
											color='warning'
											className='w-100 py-3'
											isDisable={!formik.values.loginEmail}
											onClick={handleContinue}>
											{isLoading && <Spinner isSmall inButton isGrow />}
											{t('ForgotPasswordNext')}
										</Button>
									</div>
									<div className='text-center mt-3 text-white'>
										<Link
											to='/auth-pages/login'
											className='text-decoration-none'>
											{t('Login')}
										</Link>
									</div>
								</form>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default ForgetPassword;
